import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Lock, LogOut, RefreshCw, Trash2, Mail, Phone, User, Search, Download, ChevronDown, Check, StickyNote, X } from "lucide-react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { LeafGlyph } from "./BotanicalBackdrop";
import usePageTitle from "@/hooks/usePageTitle";
import AdminLayout from "./AdminLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "hk_admin_pw";

const STATUS_OPTIONS = [
  { value: "new", label: "New", chip: "bg-[#FDECDB] text-[#B85E24] border-[#F3C89A]" },
  { value: "contacted", label: "Contacted", chip: "bg-[#EEE7D5] text-[#8A6E1F] border-[#D6C899]" },
  { value: "converted", label: "Converted", chip: "bg-[#E1EFE4] text-[#2F5A38] border-[#B7D6BE]" },
  { value: "archived", label: "Archived", chip: "bg-[#EAE7E1] text-[#4A524D] border-[#CFCAC0]" },
];
const STATUS_MAP = STATUS_OPTIONS.reduce((acc, s) => ({ ...acc, [s.value]: s }), {});

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const StatusPicker = ({ value, onChange, busy, testId }) => {
  const [open, setOpen] = useState(false);
  const current = STATUS_MAP[value] || STATUS_MAP.new;

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [open]);

  return (
    <div className="relative inline-block" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={busy}
        data-testid={testId}
        className={
          "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium border transition-colors " +
          current.chip +
          " disabled:opacity-60"
        }
      >
        {busy ? (
          <Loader2 size={11} className="animate-spin" />
        ) : (
          <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
        )}
        {current.label}
        <ChevronDown size={11} className="opacity-70" />
      </button>
      {open && (
        <div
          className="absolute z-30 mt-1 min-w-[140px] rounded-md border border-[var(--hk-border)] bg-white shadow-[0_16px_40px_-12px_rgba(30,35,31,0.18)] py-1"
          data-testid={`${testId}-menu`}
        >
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s.value}
              type="button"
              onClick={() => {
                setOpen(false);
                if (s.value !== value) onChange(s.value);
              }}
              data-testid={`${testId}-opt-${s.value}`}
              className={
                "w-full text-left px-3 py-2 text-[13px] flex items-center gap-2 hover:bg-[var(--hk-warm-ivory)] " +
                (s.value === value ? "text-[var(--hk-ink)]" : "text-[var(--hk-ink-2)]")
              }
            >
              <span className={"w-2 h-2 rounded-full border " + s.chip}></span>
              {s.label}
              {s.value === value && (
                <span className="ml-auto text-[10px] text-[var(--hk-gold-deep)] tracking-widest uppercase">
                  Current
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const LoginScreen = ({ onLoggedIn }) => {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!pw) return setErr("Password required");
    setBusy(true);
    setErr("");
    try {
      await axios.post(`${API}/admin/verify`, {}, { headers: { "X-Admin-Password": pw } });
      sessionStorage.setItem(STORAGE_KEY, pw);
      toast.success("Signed in");
      onLoggedIn(pw);
    } catch (e) {
      setErr("Invalid password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--hk-ivory)] px-6">
      <form
        onSubmit={submit}
        noValidate
        className="w-full max-w-md hk-card p-10 relative overflow-hidden"
        data-testid="admin-login-form"
      >
        <div className="flex items-center gap-2 text-[var(--hk-gold-deep)]">
          <LeafGlyph />
          <span className="label-eyebrow">HerbKranti Admin</span>
        </div>
        <h1 className="serif mt-4 text-[30px] leading-tight text-[var(--hk-ink)]">
          Enquiries console.
        </h1>
        <p className="mt-2 text-[13.5px] text-[var(--hk-ink-2)] leading-relaxed">
          Enter the admin password to view submitted enquiries.
        </p>

        <div className="mt-8 space-y-2">
          <Label
            htmlFor="admin-pw"
            className="text-[12px] tracking-[0.15em] uppercase text-[var(--hk-ink-3)]"
          >
            Admin password
          </Label>
          <div className="relative">
            <Lock
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--hk-ink-3)]"
            />
            <Input
              id="admin-pw"
              data-testid="admin-password-input"
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="••••••••••••"
              autoFocus
              className="bg-white border-[var(--hk-border)] focus-visible:ring-[var(--hk-orange)] rounded-md h-11 pl-10"
            />
          </div>
          {err && (
            <p className="text-xs text-red-600" data-testid="admin-login-error">
              {err}
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={busy}
          data-testid="admin-login-btn"
          className="hk-btn-primary mt-6 w-full justify-center disabled:opacity-70"
        >
          {busy && <Loader2 size={16} className="animate-spin" />}
          {busy ? "Verifying" : "Sign in"}
        </button>

        <p className="mt-6 text-[11px] text-[var(--hk-ink-3)] text-center tracking-wide">
          Access is restricted to HerbKranti team members.
        </p>
      </form>
    </div>
  );
};

const AdminDashboard = ({ pw, onLogout }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [deletingId, setDeletingId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [notesFor, setNotesFor] = useState(null); // enquiry object being edited
  const [notesDraft, setNotesDraft] = useState("");
  const [notesBusy, setNotesBusy] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const toggleSel = (id) =>
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const clearSel = () => setSelected(new Set());

  const openNotes = (it) => {
    setNotesFor(it);
    setNotesDraft(it.notes || "");
  };
  const closeNotes = () => setNotesFor(null);

  const saveNotes = async () => {
    if (!notesFor) return;
    setNotesBusy(true);
    try {
      await axios.patch(
        `${API}/enquiries/${notesFor.id}/notes`,
        { notes: notesDraft },
        { headers: { "X-Admin-Password": pw } }
      );
      setItems((prev) => prev.map((x) => (x.id === notesFor.id ? { ...x, notes: notesDraft } : x)));
      toast.success("Notes saved");
      closeNotes();
    } catch {
      toast.error("Failed to save notes");
    } finally {
      setNotesBusy(false);
    }
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} selected enquiries? This cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      const ids = Array.from(selected);
      await axios.post(
        `${API}/enquiries/bulk`,
        { ids, action: "delete" },
        { headers: { "X-Admin-Password": pw } }
      );
      setItems((prev) => prev.filter((x) => !selected.has(x.id)));
      clearSel();
      toast.success(`Deleted ${ids.length} enquiries`);
    } catch {
      toast.error("Bulk delete failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkStatus = async (status) => {
    if (selected.size === 0) return;
    setBulkBusy(true);
    try {
      const ids = Array.from(selected);
      await axios.post(
        `${API}/enquiries/bulk`,
        { ids, action: "status", status },
        { headers: { "X-Admin-Password": pw } }
      );
      setItems((prev) => prev.map((x) => (selected.has(x.id) ? { ...x, status } : x)));
      clearSel();
      toast.success(`Marked ${ids.length} as ${STATUS_MAP[status]?.label || status}`);
    } catch {
      toast.error("Bulk update failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/enquiries`, {
        headers: { "X-Admin-Password": pw },
      });
      setItems(res.data || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        onLogout();
      } else {
        toast.error("Failed to load enquiries");
      }
    } finally {
      setLoading(false);
    }
  }, [pw, onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this enquiry? This cannot be undone.")) return;
    setDeletingId(id);
    try {
      await axios.delete(`${API}/enquiries/${id}`, {
        headers: { "X-Admin-Password": pw },
      });
      setItems((prev) => prev.filter((x) => x.id !== id));
      toast.success("Enquiry deleted");
    } catch {
      toast.error("Failed to delete");
    } finally {
      setDeletingId(null);
    }
  };

  const handleStatusChange = async (id, nextStatus) => {
    setUpdatingId(id);
    try {
      const res = await axios.patch(
        `${API}/enquiries/${id}/status`,
        { status: nextStatus },
        { headers: { "X-Admin-Password": pw } }
      );
      setItems((prev) => prev.map((x) => (x.id === id ? { ...x, status: res.data.status } : x)));
      toast.success(`Marked ${STATUS_MAP[nextStatus]?.label || nextStatus}`);
    } catch {
      toast.error("Failed to update status");
    } finally {
      setUpdatingId(null);
    }
  };

  const escapeCsv = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const handleExportCsv = () => {
    const rows = [
      ["Received", "Name", "Phone", "Email", "Product", "Status", "Message"],
      ...filtered.map((it) => [
        it.created_at,
        it.name,
        it.phone,
        it.email,
        it.product || "",
        it.status || "new",
        it.message || "",
      ]),
    ];
    const csv = rows.map((r) => r.map(escapeCsv).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const ts = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `herbkranti-enquiries-${ts}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} ${filtered.length === 1 ? "row" : "rows"}`);
  };

  const filtered = items.filter((it) => {
    const status = (it.status || "new").toLowerCase();
    if (statusFilter !== "all" && status !== statusFilter) return false;
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (
      it.name.toLowerCase().includes(q) ||
      it.email.toLowerCase().includes(q) ||
      it.phone.toLowerCase().includes(q) ||
      (it.product || "").toLowerCase().includes(q)
    );
  });

  const counts = STATUS_OPTIONS.reduce(
    (acc, s) => ({ ...acc, [s.value]: items.filter((x) => (x.status || "new") === s.value).length }),
    {}
  );

  return (
    <AdminLayout
      title="Enquiries"
      subtitle={`${items.length} total ${items.length === 1 ? "enquiry" : "enquiries"}`}
      actions={
        <>
          <button
            onClick={handleExportCsv}
            className="hk-btn-ghost h-9 px-3 !py-0 text-[12px]"
            data-testid="admin-export-csv-btn"
            disabled={loading || items.length === 0}
          >
            <Download size={14} />
            Export CSV
          </button>
          <button
            onClick={load}
            className="hk-btn-ghost h-9 px-3 !py-0 text-[12px]"
            data-testid="admin-refresh-btn"
            disabled={loading}
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </>
      }
    >
      <div className="min-h-full">
        {/* Header eyebrow */}
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <div className="label-eyebrow">Console</div>
            <h1 className="serif mt-2 text-[36px] sm:text-[44px] leading-[1.05] text-[var(--hk-ink)]">
              {items.length}{" "}
              <span className="italic-accent text-[var(--hk-orange-deep)]">
                {items.length === 1 ? "enquiry" : "enquiries"}
              </span>{" "}
              received.
            </h1>
          </div>

          <div className="relative w-full sm:w-80">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--hk-ink-3)]"
            />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, email, product..."
              data-testid="admin-search-input"
              className="bg-white border-[var(--hk-border)] focus-visible:ring-[var(--hk-orange)] h-11 pl-10"
            />
          </div>
        </div>

        {/* Bulk actions toolbar */}
        {selected.size > 0 && (
          <div className="mb-4 hk-card p-4 flex flex-wrap items-center gap-3 bg-[var(--hk-ink)] text-white border-[var(--hk-ink)]" data-testid="admin-bulk-toolbar">
            <span className="label-eyebrow !text-white/70">{selected.size} selected</span>
            <div className="flex items-center gap-2 flex-wrap ml-auto">
              {STATUS_OPTIONS.map((s) => (
                <button
                  key={s.value}
                  onClick={() => bulkStatus(s.value)}
                  disabled={bulkBusy}
                  className="px-3 py-1.5 rounded-full text-[11px] tracking-widest uppercase bg-white/10 hover:bg-white/20 transition-colors disabled:opacity-50"
                  data-testid={`admin-bulk-status-${s.value}`}
                >
                  Mark {s.label}
                </button>
              ))}
              <button
                onClick={bulkDelete}
                disabled={bulkBusy}
                className="px-3 py-1.5 rounded-full text-[11px] tracking-widest uppercase bg-red-600/20 text-red-200 hover:bg-red-600/30 transition-colors disabled:opacity-50"
                data-testid="admin-bulk-delete"
              >
                <Trash2 size={11} className="inline mr-1" /> Delete
              </button>
              <button onClick={clearSel} className="px-3 py-1.5 rounded-full text-[11px] tracking-widest uppercase text-white/70 hover:text-white" data-testid="admin-bulk-cancel">
                Clear
              </button>
            </div>
          </div>
        )}

        {/* Status filter chips */}
        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="admin-status-filters">          <button
            type="button"
            onClick={() => setStatusFilter("all")}
            data-testid="admin-filter-all"
            className={
              "px-3.5 py-1.5 rounded-full text-[12px] tracking-wide border transition-colors " +
              (statusFilter === "all"
                ? "bg-[var(--hk-ink)] text-white border-[var(--hk-ink)]"
                : "bg-white text-[var(--hk-ink-2)] border-[var(--hk-border)] hover:border-[var(--hk-orange)]")
            }
          >
            All <span className="opacity-60 ml-1">{items.length}</span>
          </button>
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s.value}
              type="button"
              onClick={() => setStatusFilter(s.value)}
              data-testid={`admin-filter-${s.value}`}
              className={
                "px-3.5 py-1.5 rounded-full text-[12px] tracking-wide border transition-colors " +
                (statusFilter === s.value
                  ? "bg-[var(--hk-ink)] text-white border-[var(--hk-ink)]"
                  : "bg-white text-[var(--hk-ink-2)] border-[var(--hk-border)] hover:border-[var(--hk-orange)]")
              }
            >
              {s.label} <span className="opacity-60 ml-1">{counts[s.value] || 0}</span>
            </button>
          ))}
        </div>

        {/* Table */}
        {loading ? (
          <div
            className="hk-card p-16 flex items-center justify-center text-[var(--hk-ink-3)]"
            data-testid="admin-loading"
          >
            <Loader2 size={20} className="animate-spin mr-3" /> Loading enquiries...
          </div>
        ) : filtered.length === 0 ? (
          <div
            className="hk-card p-16 text-center"
            data-testid="admin-empty-state"
          >
            <LeafGlyph className="text-[var(--hk-gold)] mx-auto w-8 h-8 mb-4" />
            <h3 className="serif text-[24px] text-[var(--hk-ink)]">
              {items.length === 0 ? "No enquiries yet." : "No matches."}
            </h3>
            <p className="text-[14px] text-[var(--hk-ink-2)] mt-2">
              {items.length === 0
                ? "New submissions from the website will appear here in real time."
                : "Try a different search term."}
            </p>
          </div>
        ) : (
          <div className="hk-card overflow-hidden" data-testid="admin-enquiries-table">
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-[var(--hk-border)] bg-[var(--hk-warm-ivory)]/50">
                    <th className="px-4 py-4 w-8">
                      <input
                        type="checkbox"
                        aria-label="Select all"
                        checked={filtered.length > 0 && filtered.every((it) => selected.has(it.id))}
                        onChange={(e) => {
                          if (e.target.checked) setSelected(new Set(filtered.map((x) => x.id)));
                          else clearSel();
                        }}
                        className="h-4 w-4 rounded border-[var(--hk-border)] accent-[var(--hk-orange)]"
                        data-testid="admin-select-all"
                      />
                    </th>
                    <th className="px-6 py-4 label-eyebrow !text-[var(--hk-ink-3)]">Received</th>
                    <th className="px-6 py-4 label-eyebrow !text-[var(--hk-ink-3)]">Contact</th>
                    <th className="px-6 py-4 label-eyebrow !text-[var(--hk-ink-3)]">Product</th>
                    <th className="px-6 py-4 label-eyebrow !text-[var(--hk-ink-3)]">Status</th>
                    <th className="px-6 py-4 label-eyebrow !text-[var(--hk-ink-3)]">Message · Notes</th>
                    <th className="px-6 py-4 label-eyebrow !text-[var(--hk-ink-3)] text-right">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((it, idx) => (
                    <tr
                      key={it.id}
                      data-testid={`enquiry-row-${idx}`}
                      className={
                        "border-b border-[var(--hk-border)] transition-colors " +
                        (selected.has(it.id) ? "bg-[var(--hk-warm-ivory)]" : "hover:bg-[var(--hk-warm-ivory)]/40")
                      }
                    >
                      <td className="px-4 py-5 align-top">
                        <input
                          type="checkbox"
                          checked={selected.has(it.id)}
                          onChange={() => toggleSel(it.id)}
                          aria-label={`Select ${it.name}`}
                          className="h-4 w-4 rounded border-[var(--hk-border)] accent-[var(--hk-orange)]"
                          data-testid={`enquiry-select-${idx}`}
                        />
                      </td>
                      <td className="px-6 py-5 align-top text-[13px] text-[var(--hk-ink-2)] whitespace-nowrap">
                        {formatDate(it.created_at)}
                      </td>
                      <td className="px-6 py-5 align-top">
                        <div className="serif text-[16px] text-[var(--hk-ink)]">{it.name}</div>
                        <a
                          href={`mailto:${it.email}`}
                          className="flex items-center gap-1.5 text-[13px] text-[var(--hk-ink-2)] mt-1 hover:text-[var(--hk-orange-deep)]"
                        >
                          <Mail size={12} /> {it.email}
                        </a>
                        <a
                          href={`tel:${it.phone}`}
                          className="flex items-center gap-1.5 text-[13px] text-[var(--hk-ink-2)] mt-1 hover:text-[var(--hk-orange-deep)]"
                        >
                          <Phone size={12} /> {it.phone}
                        </a>
                      </td>
                      <td className="px-6 py-5 align-top">
                        <span className="inline-block bg-[var(--hk-warm-ivory)] border border-[var(--hk-border)] text-[12px] px-3 py-1.5 rounded-full text-[var(--hk-ink)]">
                          {it.product}
                        </span>
                      </td>
                      <td className="px-6 py-5 align-top">
                        <StatusPicker
                          value={it.status || "new"}
                          onChange={(v) => handleStatusChange(it.id, v)}
                          busy={updatingId === it.id}
                          testId={`enquiry-status-${idx}`}
                        />
                      </td>
                      <td className="px-6 py-5 align-top text-[13.5px] text-[var(--hk-ink-2)] leading-[1.6] max-w-sm">
                        {it.message ? (
                          it.message
                        ) : (
                          <span className="text-[var(--hk-ink-3)] italic">—</span>
                        )}
                        {it.notes && (
                          <div className="mt-2 pl-3 border-l-2 border-[var(--hk-gold)] text-[12.5px] text-[var(--hk-ink-2)]" data-testid={`enquiry-notes-preview-${idx}`}>
                            <span className="label-eyebrow !text-[10px] mr-1">Note</span>
                            {it.notes}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-5 align-top text-right whitespace-nowrap">
                        <button
                          onClick={() => openNotes(it)}
                          className="inline-flex items-center gap-1.5 text-[12px] text-[var(--hk-ink-2)] hover:text-[var(--hk-orange-deep)] mr-3"
                          data-testid={`enquiry-notes-btn-${idx}`}
                        >
                          <StickyNote size={12} /> Notes
                        </button>
                        <a
                          href={`https://wa.me/${(it.phone || "").replace(/[^\d]/g, "")}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 text-[12px] text-[#25D366] hover:underline mr-3"
                          data-testid={`enquiry-whatsapp-${idx}`}
                        >
                          WhatsApp
                        </a>
                        <button
                          onClick={() => handleDelete(it.id)}
                          disabled={deletingId === it.id}
                          className="inline-flex items-center gap-1.5 text-[12px] text-red-600 hover:underline disabled:opacity-50"
                          data-testid={`enquiry-delete-${idx}`}
                        >
                          <Trash2 size={12} />
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile stacked cards */}
            <div className="md:hidden divide-y divide-[var(--hk-border)]">
              {filtered.map((it, idx) => (
                <div key={it.id} className="p-5" data-testid={`enquiry-card-${idx}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <User size={14} className="text-[var(--hk-gold-deep)]" />
                        <span className="serif text-[17px] text-[var(--hk-ink)]">{it.name}</span>
                      </div>
                      <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--hk-ink-3)] mt-1">
                        {formatDate(it.created_at)}
                      </div>
                    </div>
                    <span className="text-[11px] bg-[var(--hk-warm-ivory)] border border-[var(--hk-border)] px-2 py-1 rounded-full text-[var(--hk-ink)]">
                      {it.product}
                    </span>
                  </div>
                  <div className="mt-3">
                    <StatusPicker
                      value={it.status || "new"}
                      onChange={(v) => handleStatusChange(it.id, v)}
                      busy={updatingId === it.id}
                      testId={`enquiry-status-mobile-${idx}`}
                    />
                  </div>
                  <div className="mt-3 space-y-1 text-[13px] text-[var(--hk-ink-2)]">
                    <a
                      href={`mailto:${it.email}`}
                      className="flex items-center gap-1.5 hover:text-[var(--hk-orange-deep)]"
                    >
                      <Mail size={12} /> {it.email}
                    </a>
                    <a
                      href={`tel:${it.phone}`}
                      className="flex items-center gap-1.5 hover:text-[var(--hk-orange-deep)]"
                    >
                      <Phone size={12} /> {it.phone}
                    </a>
                  </div>
                  {it.message && (
                    <p className="mt-3 text-[13.5px] text-[var(--hk-ink-2)] leading-relaxed border-t border-[var(--hk-border)] pt-3">
                      {it.message}
                    </p>
                  )}
                  <div className="mt-4 flex items-center gap-4">
                    <a
                      href={`https://wa.me/${(it.phone || "").replace(/[^\d]/g, "")}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[12px] text-[#25D366]"
                    >
                      WhatsApp
                    </a>
                    <button
                      onClick={() => openNotes(it)}
                      className="text-[12px] text-[var(--hk-ink-2)] inline-flex items-center gap-1"
                      data-testid={`enquiry-notes-btn-mobile-${idx}`}
                    >
                      <StickyNote size={12} /> Notes
                    </button>
                    <button
                      onClick={() => handleDelete(it.id)}
                      disabled={deletingId === it.id}
                      className="text-[12px] text-red-600 inline-flex items-center gap-1 disabled:opacity-50"
                    >
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Notes modal */}
      {notesFor && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={closeNotes}
          data-testid="admin-notes-modal"
        >
          <div
            className="bg-[var(--hk-ivory)] rounded-[14px] max-w-lg w-full p-8 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.4)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="label-eyebrow">Follow-up Note</div>
                <h3 className="serif text-[22px] text-[var(--hk-ink)] mt-1.5">{notesFor.name}</h3>
                <p className="text-[12px] text-[var(--hk-ink-3)] mt-1">{notesFor.email} · {notesFor.phone}</p>
              </div>
              <button onClick={closeNotes} className="w-8 h-8 rounded-full flex items-center justify-center text-[var(--hk-ink-2)] hover:bg-[var(--hk-warm-ivory)]" data-testid="admin-notes-close">
                <X size={16} />
              </button>
            </div>
            <div className="mt-5">
              <Label className="text-[12px] tracking-[0.15em] uppercase text-[var(--hk-ink-3)]">Private admin notes</Label>
              <Textarea
                value={notesDraft}
                onChange={(e) => setNotesDraft(e.target.value)}
                rows={6}
                placeholder="Called on 2026-07-14. Interested in bulk order for gifting..."
                className="mt-2 bg-white border-[var(--hk-border)] focus-visible:ring-[var(--hk-orange)] rounded-md"
                data-testid="admin-notes-input"
              />
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={closeNotes} className="hk-btn-ghost" data-testid="admin-notes-cancel">Cancel</button>
              <button onClick={saveNotes} disabled={notesBusy} className="hk-btn-primary disabled:opacity-70" data-testid="admin-notes-save">
                {notesBusy && <Loader2 size={14} className="animate-spin" />}
                Save Notes
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export const AdminPage = () => {
  usePageTitle("Admin · Enquiries");
  const pw = sessionStorage.getItem(STORAGE_KEY) || "";
  if (!pw) return <AdminLayout title="Enquiries"><div /></AdminLayout>;  // Layout shows login
  return <AdminDashboard pw={pw} onLogout={() => { sessionStorage.removeItem(STORAGE_KEY); window.location.href = "/admin"; }} />;
};

export default AdminPage;
